import {FC, useEffect, useRef, useState} from 'react'
import classNames from 'classnames'
import {useSelector} from 'react-redux'
import {useQueryClient} from '@tanstack/react-query'
import {
  selectedEndpointDebugMode,
  selectEndpoints,
} from 'features/Endpoints/endpointsSelectors'
import {dispatch} from 'app/store'
import {endpointsActions} from 'features/Endpoints/endpointsSlice'
import {markDebugSentInCache} from 'features/Endpoints/utils/markToggleSentInCache'
import {HealthStatus, SliceStatus} from 'interfaces'
import {useActions} from 'hooks'
import {useTranslation} from 'react-i18next'
import {LoadingSpinner, RenderIf, RouterPrompt} from 'components/Basic'
import {colorCodes} from 'definitions/constant'
import {ActionContainer} from 'components/Layouts'
import {Button} from 'design'
import {CanIAccess, PERMISSIONS} from 'rbac'

interface DebugMessageComponentProps {
  message: string
}

export const DebugMessageComponent: FC<DebugMessageComponentProps> = ({
  message,
}) => <p className="text-white karla-bold text-sm mr-6">{message}</p>

export const EndpointsFooter: FC = () => {
  const {selectedEndpoints, items, activeToggleRequestId, toggleResult} =
    useSelector(selectEndpoints)
  const [debugRequestId, setDebugRequestId] = useState<string | null>(null)
  const [toggleRequestId, setToggleRequestId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  // Updating the cache re-renders the list before the cleared id lands, so
  // remember the handled request and apply its result only once.
  const handledDebugRequestId = useRef<string | null>(null)
  const [showEndpointDisablePopup, setShowEndpointDisablePopup] =
    useState<boolean>(false)
  const {isSnackbarActionAvailable} = useActions()
  const {t} = useTranslation(['common', 'endpoints'])

  const isDebugMode = useSelector(selectedEndpointDebugMode)
  const isSelectedEndpointDisabled = selectedEndpoints?.isTogglePending
    ? Boolean(selectedEndpoints.disableOption)
    : selectedEndpoints?.status === HealthStatus.Disabled
  const onDisableDebugMode = (): void => {
    const action = endpointsActions.toggleEndpoint({
      debugMode: false,
      agentUUID: selectedEndpoints.agentUUID,
      userUUID: selectedEndpoints.userUUID,
    })
    setDebugRequestId(action.meta.requestId)
    dispatch(action)
    dispatch(endpointsActions.stopDiagnosticPolling())
  }

  const onDisableEndpointClick = (): void => {
    setShowEndpointDisablePopup(true)
  }

  const onDisableEndpointOkClick = (): void => {
    setShowEndpointDisablePopup(false)
    onToggleEndpoint()
  }

  const onDisableEndpointCancelClick = (): void => {
    setShowEndpointDisablePopup(false)
  }

  const onToggleEndpoint = (): void => {
    const action = endpointsActions.toggleEndpoint({
      disableOption: !isSelectedEndpointDisabled,
      agentUUID: selectedEndpoints.agentUUID,
      userUUID: selectedEndpoints.userUUID,
    })
    setToggleRequestId(action.meta.requestId)
    dispatch(action)
  }

  useEffect(() => {
    if (
      toggleRequestId &&
      (activeToggleRequestId !== toggleRequestId ||
        toggleResult?.requestId === toggleRequestId)
    ) {
      setToggleRequestId(null)
    }
    if (!debugRequestId) return
    if (activeToggleRequestId !== debugRequestId) {
      setDebugRequestId(null)
      return
    }
    if (toggleResult?.requestId !== debugRequestId) return
    setDebugRequestId(null)
    if (handledDebugRequestId.current === debugRequestId) return
    handledDebugRequestId.current = debugRequestId
    if (
      toggleResult.status !== SliceStatus.resolved ||
      toggleResult.debugMode !== false ||
      toggleResult.agentUUID !== selectedEndpoints.agentUUID ||
      toggleResult.userUUID !== selectedEndpoints.userUUID
    )
      return
    const data = {...selectedEndpoints, debugMode: false, debugLevel: 0}
    markDebugSentInCache(queryClient, data, false)
    dispatch(endpointsActions.setSelectedEndpoint(data))
    dispatch(endpointsActions.resetEndpointDiagnostic())
    dispatch(
      endpointsActions.updateEndpointList(
        items.map(endpoint =>
          endpoint.agentUUID === data.agentUUID &&
          endpoint.userUUID === data.userUUID
            ? data
            : endpoint,
        ),
      ),
    )
  }, [
    activeToggleRequestId,
    debugRequestId,
    toggleRequestId,
    toggleResult,
    selectedEndpoints,
    items,
    queryClient,
  ])

  return (
    <ActionContainer
      className={classNames(
        'border-t-0.5 border-gray-400 flex justify-end items-center',
        {
          'bg-dope-teal-200': isDebugMode,
          'bg-dope-black': !isDebugMode,
          hidden: !isSnackbarActionAvailable,
        },
      )}
    >
      <CanIAccess
        reqPermissions={[PERMISSIONS.ENABLE_DEBUG.WRITE]}
        fallbackComponent={
          <DebugMessageComponent
            message={t('common:DEBUG_MODE_FOOTER_MESSAGE_WITHOUT_CLICK')}
          />
        }
      >
        <>
          <DebugMessageComponent
            message={t('common:DEBUG_MODE_FOOTER_MESSAGE')}
          />

          <Button
            className="mr-4 w-40 h-8"
            size="xsm"
            fontWeight="bold"
            borderRadius="rounded"
            data-cy="exit-debug-mode"
            aria-label={t('common:EXIT_DEBUG_MODE')}
            onClick={onDisableDebugMode}
          >
            {!debugRequestId ? (
              t('common:EXIT_DEBUG_MODE')
            ) : (
              <LoadingSpinner
                type="TailSpin"
                color={colorCodes.dopeBlack}
                width={16}
                height={16}
              />
            )}
          </Button>
        </>
      </CanIAccess>
      <CanIAccess reqPermissions={[PERMISSIONS.DISABLE_ENDPOINT.WRITE]}>
        <Button
          className="mr-3 h-8"
          colorScheme="cyan"
          size="xsm"
          borderRadius="rounded"
          fontWeight="bold"
          onClick={onDisableEndpointClick}
          data-cy="toggle-endpoint-bottom-panel"
        >
          <RenderIf isTrue={!toggleRequestId}>
            {isSelectedEndpointDisabled
              ? t('common:ENABLE_ENDPOINT')
              : t('common:DISABLE_ENDPOINT')}
          </RenderIf>
          <RenderIf isTrue={Boolean(toggleRequestId)}>
            <LoadingSpinner
              type="TailSpin"
              color={colorCodes.cyanBright}
              width={16}
              height={16}
            />
          </RenderIf>
        </Button>
      </CanIAccess>
      <RenderIf isTrue={showEndpointDisablePopup}>
        <RouterPrompt
          title={
            isSelectedEndpointDisabled
              ? t('endpoints:ENDPOINT_ENABLE_CONFIRMATION_MESSAGE')
              : t('endpoints:ENDPOINT_DISABLE_CONFIRMATION_MESSAGE')
          }
          onCancel={onDisableEndpointCancelClick}
          onOK={onDisableEndpointOkClick}
        />
      </RenderIf>
    </ActionContainer>
  )
}
